// ============================================================================
// DOCUMENTATION SERVICE - SERVICIO PARA GESTIÓN DE DOCUMENTOS CON AUTENTICACIÓN
// ============================================================================

import apiClient from './apiClient';

// Tope de documentos anclados (favoritos) que se muestran destacados, tanto en
// el resumen de la pantalla de documentación como en el panel del dashboard.
// Es un límite VISUAL: marcar favoritos sigue siendo libre.
export const MAX_PINNED_DOCS = 6;

/**
 * Servicio centralizado para todas las operaciones de documentos
 */
class DocumentationService {
  
  /**
   * Realizar petición HTTP con manejo de errores y autenticación
   */
  async makeRequest(endpoint, options = {}, token = null) {
    try {
      // El apiClient deriva la base URL por tenant, pone el Bearer y lanza en !ok.
      const data = await apiClient.request(endpoint, { ...options, token });

      // Preservar el comportamiento histórico: un envelope 200 con status:error
      // también se trata como fallo.
      if (data && data.status === 'error') {
        throw new Error(data.message || 'Error desconocido del servidor');
      }

      return data;
    } catch (error) {
      console.error('❌ API Error:', error);
      throw error;
    }
  }

  /**
   * Realizar petición para upload de archivos con autenticación
   */
  async makeUploadRequest(endpoint, formData, token = null) {
    try {
      // Subida multipart: el apiClient NO fija Content-Type (boundary automático).
      const data = await apiClient.upload(endpoint, formData, { token });

      if (data && data.status === 'error') {
        throw new Error(data.message || 'Error desconocido del servidor');
      }

      return data;
    } catch (error) {
      console.error('❌ Upload Error:', error);
      throw error;
    }
  }

  /**
   * Obtener documento específico por ID
   */
  async getDocument(id, token = null) {
    try {
      console.log('📄 Getting document by ID:', id);
      const response = await this.makeRequest(`documento&id=${id}`, {}, token);
      return response.data;
    } catch (error) {
      console.error('❌ Error getting document:', error);
      throw error;
    }
  }

  /**
   * Crear nuevo documento
   */
  async createDocument(documentData, token = null) {
    try {
      console.log('📝 Creating new document:', documentData);
      const response = await this.makeRequest('create_documento', {
        method: 'POST',
        body: JSON.stringify(documentData)
      }, token);
      console.log('✅ Document created successfully');
      return response.data;
    } catch (error) {
      console.error('❌ Error creating document:', error);
      throw error;
    }
  }

  /**
   * Actualizar documento existente
   */
  async updateDocument(id, documentData, token = null) {
    try {
      console.log('✏️ Updating document:', { id, documentData });
      // ✅ CORREGIDO: El backend espera el ID en query parameter, no en el body
      const response = await this.makeRequest(`update_documento&id=${id}`, {
        method: 'PUT',
        body: JSON.stringify(documentData) // Enviar solo documentData, sin el id
      }, token);
      console.log('✅ Document updated successfully');
      return response.data;
    } catch (error) {
      console.error('❌ Error updating document:', error);
      throw error;
    }
  }

  /**
   * Eliminar documento
   */
  async deleteDocument(id, token = null) {
    try {
      console.log('🗑️ Deleting document:', id);

      // El backend espera el ID como query param; el endpoint hace DELETE lógico vía POST.
      const response = await this.makeRequest(`delete_documento&id=${id}`, {
        method: 'POST'
      }, token);

      console.log('✅ Document deleted successfully');
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting document:', error);
      throw error;
    }
  }

  /**
   * Subir archivo PDF
   */
  async uploadPDF(file, token = null) {
    try {
      console.log('📤 Uploading PDF file:', { 
        name: file.name, 
        size: file.size, 
        type: file.type 
      });
      
      const formData = new FormData();
      formData.append('pdf', file);
      
      const response = await this.makeUploadRequest('upload_pdf', formData, token);
      console.log('✅ PDF uploaded successfully');
      return response.data;
    } catch (error) {
      console.error('❌ Error uploading PDF:', error);
      throw error;
    }
  }

  /**
   * Obtener documentos por tipo
   */
  async getDocumentsByType(type, token = null) {
    try {
      console.log('📂 Getting documents by type:', type);
      const response = await this.makeRequest(`documentos_tipo&tipo=${type}`, {}, token);
      return response.data || [];
    } catch (error) {
      console.error('❌ Error getting documents by type:', error);
      throw error;
    }
  }

  /**
   * Buscar documentos
   */
  async searchDocuments(query, token = null) {
    try {
      console.log('🔍 Searching documents:', query);
      const response = await this.makeRequest(`search_documentos&q=${encodeURIComponent(query)}`, {}, token);
      return response.data || [];
    } catch (error) {
      console.error('❌ Error searching documents:', error);
      throw error;
    }
  }

  /**
   * Obtener documentos vinculados a una tarea
   */
  async getDocumentsByTask(taskId, token = null) {
    try {
      console.log('📋 Getting documents by task:', taskId);
      const response = await this.makeRequest(`documentos_tarea&task_id=${taskId}`, {}, token);
      return response.data || [];
    } catch (error) {
      console.error('❌ Error getting documents by task:', error);
      throw error;
    }
  }

  /**
   * Obtener tareas vinculadas a un documento
   */
  async getTasksByDocument(documentId, token = null) {
    try {
      console.log('📄 Getting tasks by document:', documentId);
      const response = await this.makeRequest(`tarea_documentos&documento_id=${documentId}`, {}, token);
      return response.data || [];
    } catch (error) {
      console.error('❌ Error getting tasks by document:', error);
      throw error;
    }
  }

  /**
   * Vincular documento a tarea
   */
  async linkDocumentToTask(documentId, taskId, vinculadoPor = 1, token = null) {
    try {
      console.log('🔗 Linking document to task:', { documentId, taskId, vinculadoPor });
      const response = await this.makeRequest('link_documento_tarea', {
        method: 'POST',
        body: JSON.stringify({
          documento_id: documentId,
          tarea_id: taskId,
          vinculado_por: vinculadoPor
        })
      }, token);
      console.log('✅ Document linked to task successfully');
      return response.data;
    } catch (error) {
      console.error('❌ Error linking document to task:', error);
      throw error;
    }
  }

  /**
   * Desvincular documento de tarea
   */
  async unlinkDocumentFromTask(documentId, taskId, token = null) {
    try {
      console.log('🔓 Unlinking document from task:', { documentId, taskId });
      const response = await this.makeRequest(
        `unlink_documento_tarea&documento_id=${documentId}&tarea_id=${taskId}`, 
        { method: 'DELETE' },
        token
      );
      console.log('✅ Document unlinked from task successfully');
      return response.data;
    } catch (error) {
      console.error('❌ Error unlinking document from task:', error);
      throw error;
    }
  }

  /**
   * Crear documento completo con archivo PDF
   */
  async createDocumentWithPDF(documentData, file, token = null) {
    try {
      console.log('📝📤 Creating document with PDF');
      
      // 1. Subir el archivo PDF primero
      const uploadResult = await this.uploadPDF(file, token);
      
      // 2. Crear el documento con la información del archivo
      const completeDocumentData = {
        ...documentData,
        tipo: 'pdf',
        archivo_nombre: uploadResult.archivo_nombre,
        archivo_ruta: uploadResult.archivo_ruta,
        archivo_tamano: uploadResult.archivo_tamano
      };

      // 3. Crear el documento
      const documentResult = await this.createDocument(completeDocumentData, token);
      
      console.log('✅ Document with PDF created successfully');
      return {
        ...documentResult,
        upload_info: uploadResult
      };
    } catch (error) {
      console.error('❌ Error creating document with PDF:', error);
      throw error;
    }
  }

  /**
   * Obtener documentos recientes
   */
  async getRecentDocuments(limit = 10, token = null) {
    try {
      console.log('⏰ Getting recent documents:', limit);
      const response = await this.makeRequest(`documentos_recientes&limit=${limit}`, {}, token);
      return response.data || [];
    } catch (error) {
      console.error('❌ Error getting recent documents:', error);
      throw error;
    }
  }

  /**
   * Verificar si un documento ya está vinculado a una tarea
   */
  async checkDocumentTaskLink(documentId, taskId, token = null) {
    try {
      console.log('🔍 Checking document-task link:', { documentId, taskId });
      const response = await this.makeRequest(
        `check_documento_tarea&documento_id=${documentId}&tarea_id=${taskId}`,
        {},
        token
      );
      return response.data;
    } catch (error) {
      console.error('❌ Error checking document-task link:', error);
      return { isLinked: false };
    }
  }

  /**
   * Validar documento antes de operaciones
   */
  async validateDocumentTaskLink(documentId, taskId, token = null) {
    try {
      console.log('✅ Validating document-task link:', { documentId, taskId });
      
      // Verificar que el documento existe
      const document = await this.getDocument(documentId, token);
      if (!document) {
        throw new Error('Documento no encontrado');
      }

      // Verificar que no existe ya la vinculación
      const linkCheck = await this.checkDocumentTaskLink(documentId, taskId, token);
      if (linkCheck && linkCheck.isLinked) {
        throw new Error('El documento ya está vinculado a esta tarea');
      }

      return true;
    } catch (error) {
      console.error('❌ Validation failed:', error);
      throw error;
    }
  }

  /**
   * Validar URL
   */
  isValidUrl(url) {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Formatear tamaño de archivo
   */
  formatFileSize(bytes) {
    if (!bytes) return '';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Obtener icono según tipo de documento
   */
  getDocumentIcon(tipo) {
    switch (tipo) {
      case 'pdf': return '📄';
      case 'enlace': return '🔗';
      case 'documento': return '📝';
      default: return '📄';
    }
  }

  /**
   * Obtener etiqueta de tipo de documento
   */
  getDocumentTypeLabel(tipo) {
    switch (tipo) {
      case 'pdf': return 'PDF';
      case 'enlace': return 'Enlace';
      case 'documento': return 'Documento';
      default: return 'Documento';
    }
  }

  /**
   * Obtener estadísticas de documentos
   */
  async getDocumentStats(token = null) {
    try {
      console.log('📊 Getting document statistics');
      const response = await this.makeRequest('document_stats', {}, token);
      return response.data;
    } catch (error) {
      console.error('❌ Error getting document stats:', error);
      throw error;
    }
  }

  /**
   * Obtener estadísticas de vinculaciones
   */
  async getDocumentTaskStats(token = null) {
    try {
      console.log('📊 Getting document-task statistics');
      const response = await this.makeRequest('document_task_stats', {}, token);
      return response.data;
    } catch (error) {
      console.error('❌ Error getting document-task stats:', error);
      throw error;
    }
  }

  /**
   * Obtener categorías de documentación
   */
  async getCategorias(token = null) {
    try {
      const response = await this.makeRequest('categorias_documentos', {}, token);
      return response.data || [];
    } catch (error) {
      console.error('❌ Error getting categorías:', error);
      throw error;
    }
  }

  /**
   * Crear nueva categoría
   */
  async createCategoria({ nombre, color }, token = null) {
    try {
      const response = await this.makeRequest('create_categoria_documento', {
        method: 'POST',
        body: JSON.stringify({ nombre, color })
      }, token);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating categoría:', error);
      throw error;
    }
  }

  /**
   * Toggle favorito de un documento
   */
  async toggleFavorito(id, token = null) {
    try {
      const response = await this.makeRequest(`toggle_favorito_documento&id=${id}`, {
        method: 'POST'
      }, token);
      return response.data;
    } catch (error) {
      console.error('❌ Error toggling favorito:', error);
      throw error;
    }
  }

}

// Exportar una instancia singleton
const documentationService = new DocumentationService();
export default documentationService;